/**
 * Отчёт по критериям (#866): вкладка «Критерии» рядом с «Головне».
 * Решения — `docs/plans/2026-09-30-criteria-report.md`, спека программы —
 * `docs/plans/2026-09-18-custom-criteria.md` §6.2.
 *
 * Два маршрута: сводка по всем критериям организации и один критерий — во
 * времени и по менеджерам. Агрегаты — только Postgres `record_signals`.
 */
import { z } from 'zod/v4';
import { criterionIdSchema } from './criteria-filter';
import { reportSummaryQuerySchema } from './reports';

/**
 * Запрос обоих маршрутов: область и период — как у «Головне». `timeZone` —
 * зона смотрящего: дни и недели режутся по ней (неизвестная — по `tzOffset`,
 * который маршрут читает сам и ограничивает, а не отвергает).
 */
export const criteriaReportQuerySchema = reportSummaryQuerySchema.extend({
  timeZone: z.string().max(64).optional(),
});

/** `GET /api/v1/reports/criteria/:criterionId` — тот же формат id, что у фильтра списка. */
export const criteriaReportParamSchema = z.object({ criterionId: criterionIdSchema });

/** Корзины оценки 0–100 в таблице распределения: пять по двадцать. */
export const CRITERIA_REPORT_SCORE_BUCKETS = [
  { from: 0, to: 19 },
  { from: 20, to: 39 },
  { from: 40, to: 59 },
  { from: 60, to: 79 },
  { from: 80, to: 100 },
] as const;

/** Окно длиннее — недели, иначе дни. */
export const CRITERIA_REPORT_DAY_BUCKETS_MAX_DAYS = 31;

/** Потолок строк таблицы менеджеров: больше в одной организации не бывает. */
export const CRITERIA_REPORT_AGENTS_LIMIT = 500;

const countSchema = z.number().int().nonnegative();
/** Среднее оценки; не ограничено 0–100 намеренно — хранится то, что записал шаг. */
const scoreAverageSchema = z.number().nullable();

/**
 * Знаменатели рядом с долей (спека §6.2). «Не оценено» — звонки периода в
 * области критерия без строки; оценка сверху: пауза критерия нигде не
 * записана, и её звонки тоже здесь.
 */
export const criteriaReportCountsSchema = z.object({
  /** Строки с ответом: применимо и не «не уверен». */
  evaluated: countSchema,
  notApplicable: countSchema,
  /** Модель воздержалась (§3.4): ни значения, ни вердикта. */
  unsure: countSchema,
  /** `null` — критерий сейчас не оценивает (пауза, архив): счёт не ведётся. */
  notEvaluated: countSchema.nullable(),
});

export const criteriaReportChoiceSchema = z.object({
  code: z.string(),
  count: countSchema,
});

/** Значение одного критерия за период или корзину: заполнено по типу. */
export const criteriaReportValueSchema = z.object({
  /** Флаг: сколько сработало из `evaluated`. Иначе null. */
  triggered: countSchema.nullable(),
  /** Оценка: среднее. Иначе null; у оценки без ответов — тоже null. */
  scoreAverage: scoreAverageSchema,
  /** Список: число по каждому коду, включая «в разговоре не найдено». */
  choices: z.array(criteriaReportChoiceSchema).nullable(),
});

export const criteriaReportCriterionSchema = z.object({
  id: z.string(),
  /**
   * Фильтр «Записей» умеет выразить этот критерий: живой или среди последних
   * архивных с ответами, что предлагает перечень фильтра. Нет — числа без
   * ссылок: список отбросил бы условие и показал звонки без него.
   */
  linkable: z.boolean(),
  name: z.string(),
  resultType: z.enum(['flag', 'score', 'choice']),
  archived: z.boolean(),
  isActive: z.boolean(),
  /**
   * «Действует с …»: создание критерия — единственная надёжная дата. `null` —
   * дата в базе неразборчива, отметки нет.
   */
  activeSince: z.string().nullable(),
  /** Варианты списка с архивными — подписи кодов. Не список — null. */
  options: z
    .array(z.object({ code: z.string(), label: z.string(), archived: z.boolean() }))
    .nullable(),
  counts: criteriaReportCountsSchema,
  value: criteriaReportValueSchema,
  /**
   * «Формулировка изменена с …»: первый звонок периода, оценённый каждой
   * версией, начиная со второй. Считаются строки всех версий (решение Р2).
   */
  versionChanges: z.array(z.object({ version: z.number().int(), since: z.string() })),
});

/** Эхо области: ссылка в список называет ровно посчитанное, как у «Головне». */
export const reportScopeEchoShape = {
  periodStart: z.string(),
  periodEnd: z.string(),
  projectIds: z.array(z.string()).nullable(),
};

export const criteriaReportResponseSchema = z.object({
  ...reportScopeEchoShape,
  criteria: z.array(criteriaReportCriterionSchema),
});

export const criteriaReportPointSchema = z.object({
  start: z.string(),
  end: z.string(),
  evaluated: countSchema,
  triggered: countSchema.nullable(),
  scoreAverage: scoreAverageSchema,
});

export const criteriaReportAgentSchema = z.object({
  agentId: z.string(),
  agentName: z.string().nullable(),
  /**
   * Установка, если строка — сотрудник CRM без аккаунта Aelo (#1927): один и тот
   * же id в двух порталах — двое. Необязательное: edge-api деплоится раньше
   * ui-worker, и ответ без поля остаётся разборным.
   */
  installationId: z.string().nullable().optional(),
  /**
   * Портал этой установки: подпись, которой одноимённые строки из двух порталов
   * различаются в таблице. `null` — у строки нет установки или портал неизвестен.
   */
  portal: z.string().nullable().optional(),
  /**
   * `agent_id`, по которому список звонков совпадёт с числами строки (у связанного
   * с аккаунтом менеджера — его CRM-id, не `agentId` строки). `null` — такого id
   * нет: строка сложена из записей под несколькими `agent_id` (CRM-id и id
   * аккаунта) либо тот же id есть у другого человека таблицы (портал), и число не
   * открывает список. Поля нет (старый edge-api) — список открывается по `agentId`.
   */
  recordsAgentId: z.string().nullable().optional(),
  counts: criteriaReportCountsSchema.omit({ notEvaluated: true }),
  value: criteriaReportValueSchema,
});

export const criteriaReportDetailResponseSchema = z
  .object({
    ...reportScopeEchoShape,
    bucket: z.enum(['day', 'week']),
    /**
     * Итоги, знаменатели и отметки критерия — в сводке (`criteriaReportResponseSchema`):
     * деталь их не пересчитывает.
     */
    criterionId: z.string(),
    series: z.array(criteriaReportPointSchema),
    /** Оценка: распределение по `CRITERIA_REPORT_SCORE_BUCKETS`. Иначе null. */
    scoreBuckets: z
      .array(
        z.object({ from: z.number().int(), to: z.number().int(), count: countSchema })
      )
      .nullable(),
    agents: z.array(criteriaReportAgentSchema).max(CRITERIA_REPORT_AGENTS_LIMIT),
    /**
     * Сколько менеджеров было в периоде всего (#1364): `agents` обрезан по
     * `CRITERIA_REPORT_AGENTS_LIMIT`, и при `agentsTotal > agents.length` список
     * неполный. Необязателен: ответ edge-api до этого поля интерфейс читает как
     * «не обрезано».
     */
    agentsTotal: z.number().int().nonnegative().optional(),
  })
  .refine(
    (body) => body.agentsTotal === undefined || body.agentsTotal >= body.agents.length,
    {
      message: 'agentsTotal cannot be less than the number of agents returned',
      path: ['agentsTotal'],
    }
  );

export type CriteriaReportQuery = z.infer<typeof criteriaReportQuerySchema>;
export type CriteriaReportCounts = z.infer<typeof criteriaReportCountsSchema>;
export type CriteriaReportValue = z.infer<typeof criteriaReportValueSchema>;
export type CriteriaReportCriterion = z.infer<typeof criteriaReportCriterionSchema>;
export type CriteriaReportResponse = z.infer<typeof criteriaReportResponseSchema>;
export type CriteriaReportPoint = z.infer<typeof criteriaReportPointSchema>;
export type CriteriaReportAgent = z.infer<typeof criteriaReportAgentSchema>;
export type CriteriaReportDetailResponse = z.infer<
  typeof criteriaReportDetailResponseSchema
>;
